// The tower defence simulation. The enemy walks a road from the edge of the map to the Command
// Center; the player's towers stand either side of it and shoot what passes; whatever reaches the
// end wears the Command Center down, and when it is gone the game is over. Offshore, out of reach
// of anything the player builds, the enemy's fleet holds station and launches missiles and drones
// straight at the Command Center: the shield has to stop those in the air. Pure, deterministic and
// integer-only, like the raid sim it grew out of (see SPEC.md 5.2): the same seed and the same
// clicks give the same game, and its snapshots are the same shape, so the 3D scene draws them
// unchanged. The towers and the enemy are the defences and units of data.ts; their numbers, prices
// and rewards are in td-balance.ts, the roads in td-maps.ts and the waves in td-waves.ts.
import { MAP_SIZE, TICKS_PER_SECOND, TILE, dist, distToRect } from './fixed.ts';
import { SEA_WIDTH } from './grid.ts';
import { Rng } from './rng.ts';
import { PLACE_MARGIN } from './village.ts';
import { hpForLevel, interceptChanceVs, threatOf, type AbilityDef, type AbilityKind, type BuildingDef, type GameConfig, type Threat, type UnitDef } from './data.ts';
import type { CombatEvent, Shot, Snapshot, UnitSnapshot, WarheadInbound } from './types.ts';
import { HQ_SIZE, buildRoad, legDir, roadPoint, type Road, type TdMap } from './td-maps.ts';
import { BREACH, ENEMY_SPEED_PCT, GROUND_HP_PCT, TOWER_DAMAGE_PCT, groundBlast, groundDamage, rewardPct, PATROL_MAX_SPEED, PATROL_TARGETS, GUNSHIP_WEAPONS, type GunWeapon, FLEET_APPROACH, FLEET_SPEED_TPS, FLEET_STATION, EARLY_CALL_BONUS_PCT, maxLevel, towerDefAt, PRICE, REWARD, TD_CONFIG, TOWERS, WAVE_QUIET_TICKS, damageAt, rangeAt, rebuildPrice, sellValue, upgradePrice, waveBonus } from './td-balance.ts';
import { armourPct, tdWave, type TdShip, type TdWave } from './td-waves.ts';

/** Which enemy a tower shoots first: the one furthest down the road, the last, the toughest, or the nearest. */
export type TargetMode = 'first' | 'last' | 'strong' | 'close';
export const TARGET_MODES: readonly TargetMode[] = ['first', 'last', 'strong', 'close'];
export const TARGET_MODE_NAMES: Record<TargetMode, string> = { first: 'First', last: 'Last', strong: 'Strongest', close: 'Closest' };

export interface Tower {
  id: number;
  type: string;
  def: BuildingDef;
  x: number;          // tile
  y: number;
  level: number;
  hp: number;
  maxHp: number;
  /** Standing. A tower at 0 hp is rubble: still placed, firing nothing, until it is rebuilt. */
  alive: boolean;
  /** Sold or spent (a mine that went off): it is no longer on the map at all. */
  gone: boolean;
  cooldown: number;
  burstPos: number;
  ammo: number;
  targetId: number;
  /** Enemies an interceptor from this tower has already missed: it does not waste another on them. */
  missed: Set<number>;
  mode: TargetMode;
  priority: Threat[];
  /** It has fired this wave. */
  fired: boolean;
  /** The tick it last fired, for the turret animation. */
  firedTick: number;
}

export interface Enemy {
  id: number;
  type: string;
  def: UnitDef;
  /** How far along its road it is, in units, and which of the map's roads it walks. */
  s: number;
  lane: number;
  x: number;
  y: number;
  hp: number;
  /** Its hit points when it stepped onto the road: the waves past the hand-made ones come tougher. */
  maxHp: number;
  alive: boolean;
  /** It reached the Command Center. */
  through: boolean;
  /** Its place across the road, units either side of the centre line, so a column is not a queue. */
  off: number;
  cooldown: number;
  burstPos: number;
  /** The tower it is shooting at, or -1. */
  targetId: number;
  /** A warhead in its terminal dive: ticks until it lands, -1 for anything else. */
  fuse: number;
  /** Where it entered, and where a warhead is aimed, units: the arc it is drawn on. */
  spawnX: number;
  spawnY: number;
  lockX: number;
  lockY: number;
  /** Fired on: ticks it keeps jinking, and which way it breaks. */
  jinkTicks: number;
  jinkDir: number;
  /** What a warhead is going for; null for anything on the road. */
  aim: WarheadAim | null;
  /** A warhead that came down on the towers rather than the Command Center: neither a leak nor a kill. */
  landed: boolean;
  /** In the air, flying free of any lane straight at the tower with this id: 0 is the Command Center. -1 for anything on the road. */
  flyAt: number;
  /** The wave it came with: what its kill pays depends on it. */
  wave: number;
  /** The ship that launched it, by type; undefined for anything from beyond the map. */
  launcher?: string;
  /** Waypoints still to fly, in order, before the run at what it is locked on: a flight off a ship goes round, not straight in. */
  route: [x: number, y: number][];
  /** Its own speed against the book's, in percent: no two in a column walk in step. */
  pace: number;
  /** Set alight by incendiary rounds: ticks it burns on, and what it loses every so often meanwhile. */
  burnTicks?: number;
  burn?: { dmg: number; every: number };
}

/** Where a warhead is going: the Command Center, or the towers it can hurt most. */
export type WarheadAim = 'hq' | 'towers';

/**
 * A ship of the enemy's fleet: it sails in from the open sea to its station, FLEET_STATION tiles
 * off the coast, launches what it carries at the Command Center one at a time, and sails back out.
 * Nothing the player builds reaches it, so it has no hit points to lose and is not an Enemy.
 */
export interface Ship {
  id: number;
  type: string;
  def: UnitDef;
  x: number;
  y: number;
  /** What it launches, and how many it has left. */
  unit: string;
  left: number;
  gap: number;
  /** Ticks until its next launch, once on station. */
  launchIn: number;
  /** Sailing in, on station, or sailing back out with empty launchers. */
  state: 'in' | 'station' | 'out';
  /** The tick it sails in. */
  at: number;
  wave: number;
}

/** A unit called in as support: a tank, a helicopter, a drone or a fighter that hunts within reach of the tile it was sent to, for the rest of the wave. */
export interface Patrol {
  id: number;
  type: string;
  /** The point it was called to, in units, and how far from it it goes. */
  hx: number;
  hy: number;
  radius: number;
  def: UnitDef;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  cooldown: number;
  burstPos: number;
  targetId: number;
}

/** A missile in flight from a tower, locked on one enemy for good. */
interface Projectile {
  id: number;
  from: number;
  launchX: number;
  launchY: number;
  targetId: number;
  x: number;
  y: number;
  tx: number;
  ty: number;
  speed: number;
  damage: number;
  vx: number;
  vy: number;
  coasting: boolean;
  coastLeft: number;
  burn: number;
}

/** An enemy still to come: which, and the tick it steps onto the road. */
interface Pending { at: number; unit: string; wave: number }

/** A strike called in on the ground, landing at tick `at`: a fire mission, or the player's own nuke. */
interface FireMission { at: number; tx: number; ty: number; kind: 'artillery' | 'nuke' }
/**
 * An A-10 on its run: in from `approach` units before the stretch it rakes, firing along `fire` units
 * of the road, then climbing away; `along` is how far it has flown. Units, and a unit vector (x1000).
 */
interface Strafe { id: number; sx: number; sy: number; dx: number; dy: number; along: number; approach: number; fire: number; damage: number; width: number }
/** How fast the A-10 flies its run, units a tick, and how far before and after the stretch it is drawn. */
const STRAFE_SPEED = Math.floor((9 * TILE) / TICKS_PER_SECOND);
const STRAFE_LEAD = TILE * 10;
/** How far a Lancet sees a support unit to hunt, and how close it has to get to set its warhead off. */
const HUNT_REACH = TILE * 12;
const HUNT_HIT = Math.floor(TILE * 0.6);

/** A round from the gunship on its way down. */
interface GunShell { at: number; x: number; y: number; weapon: GunWeapon }

/** What happened this tick that the raid's combat feed does not say: for the game's own HUD. */
export type TdEvent =
  | { kind: 'wave'; n: number; wave: TdWave }
  | { kind: 'leak'; type: string; lives: number; id: number }
  | { kind: 'cleared'; n: number; bonus: number }
  | { kind: 'tower-lost'; type: string; id: number }
  | { kind: 'won' }
  | { kind: 'fallout'; lives: number }
  | { kind: 'lost' };

export type Phase = 'build' | 'wave' | 'lost';

/**
 * Everything the player does, as the sim records it: with the map and the seed, the log replays
 * to the same game, so a game is saved as its log and restored by playing it back (see `replay`).
 */
export type TdCommand =
  | { tick: number; kind: 'place'; type: string; x: number; y: number }
  | { tick: number; kind: 'upgrade'; id: number }
  | { tick: number; kind: 'sell'; id: number }
  | { tick: number; kind: 'rebuild'; id: number }
  | { tick: number; kind: 'mode'; id: number; mode: TargetMode }
  | { tick: number; kind: 'priority'; id: number; priority: Threat[] }
  | { tick: number; kind: 'wave' }
  | { tick: number; kind: 'gunship'; weapon: GunWeapon; x: number; y: number }
  | { tick: number; kind: 'ability'; ability: AbilityKind; x: number; y: number };

/** How far past the road's end an enemy counts as inside the Command Center. */
const HQ_REACH = TILE;
/** How long an interceptor that missed flies on before it blows itself up. */
const SELF_DESTRUCT_TICKS = TICKS_PER_SECOND;
const COAST_MARGIN = TILE * 6;
/** How much quicker or slower than the book each enemy moves, in percent, so a column strings out along the road. */
const PACE_MIN_PCT = 88, PACE_MAX_PCT = 112;

/** How far beyond the map's edge anything airborne enters, in tiles. */
const SKY_APPROACH = 6;
/** How far along the north or south edge something off a ship turns in, as a share of the map: the far corner is too long a flight. */
const SEA_ROUTE_REACH = 0.6;

/** A missile this close makes a jinking aircraft break hard. */
const JINK_RANGE = TILE * 6;
/** A nuclear warhead can be engaged only in its terminal phase: this share (percent) of its flight, the last of it, coming down. */
const TERMINAL_PCT = 35;
const JINK_TICKS = 2 * TICKS_PER_SECOND;
/** A one-way drone dives on a tower this near the road. */
const DIVE_REACH = TILE * 2;
/** What a fortified tower shrugs off: rifles, machine guns and light drone or helicopter fire. */
const SMALL_ARMS: ReadonlySet<Threat> = new Set<Threat>(['infantry', 'drone', 'aircraft']);
/** Ticks between a fire mission's call and its shells landing (the ability's own countdown). */
const ARTILLERY_FLIGHT = 3 * TICKS_PER_SECOND;
/** Ticks a mine takes to re-arm after it goes off. */
const MINE_REARM_TICKS = 8 * TICKS_PER_SECOND;

const isAir = (def: UnitDef): boolean => def.domain === 'aircraft' || def.domain === 'drone' || def.domain === 'missile';
const isWarhead = (def: UnitDef): boolean => def.warhead !== undefined && def.speed === 0;

/** A blast's damage at `d` units from its centre: full there, half at its edge `r`, nothing past it. */
function splashDamage(damage: number, d: number, r: number): number {
  return d > r ? 0 : damage - Math.floor((damage * d) / (2 * r));
}

/** What a gun's tracers carry for the view: explosive rounds over incendiary, or nothing for ball. */
function roundOf(def: BuildingDef): { round?: 'incendiary' | 'explosive' } {
  return def.explosive ? { round: 'explosive' } : def.incendiary ? { round: 'incendiary' } : {};
}

function afterShot(e: { cooldown: number; burstPos: number }, def: { attackTicks?: number; burst?: number; burstGap?: number }): void {
  e.burstPos++;
  if (e.burstPos < (def.burst ?? 1)) { e.cooldown = def.burstGap ?? 1; return; }
  e.burstPos = 0;
  e.cooldown = def.attackTicks ?? 1;
}

/** The chance of at least `k` successes in `n` independent tries at probability `p` each. */
function atLeast(k: number, n: number, p: number): number {
  if (k <= 0) return 1;
  if (k > n) return 0;
  let fail = 0, term = Math.pow(1 - p, n);
  // the terms of the binomial for 0..k-1 successes, each from the one before
  for (let i = 0; i < k; i++) {
    fail += term;
    term = (term * (n - i) * p) / ((i + 1) * (1 - p));
  }
  return Math.max(0, Math.min(1, 1 - fail));
}

function fnv(h: number, v: number): number {
  h ^= v & 0xff; h = Math.imul(h, 0x01000193);
  h ^= (v >>> 8) & 0xff; h = Math.imul(h, 0x01000193);
  h ^= (v >>> 16) & 0xff; h = Math.imul(h, 0x01000193);
  h ^= (v >>> 24) & 0xff; h = Math.imul(h, 0x01000193);
  return h >>> 0;
}

export type PlaceError = 'unknown' | 'off-map' | 'on-road' | 'off-road' | 'occupied' | 'poor';

export const PLACE_ERROR_TEXT: Record<PlaceError, string> = {
  unknown: 'Not a tower', 'off-map': 'Off the map', 'on-road': 'Not on the road', 'off-road': 'Mines go on the road', occupied: 'Something is there', poor: 'Not enough cash',
};

export class TdSim {
  readonly config: GameConfig;
  readonly road: Road;
  private readonly rng: Rng;
  tick = 0;
  cash: number;
  readonly maxIntegrity: number;
  /** Waves started so far; `cleared` of them are over. */
  wave = 0;
  cleared = 0;
  phase: Phase = 'build';
  /** The map's waves are held: the game goes on, but it is won. */
  won = false;
  kills = 0;
  leaks = 0;
  /** The towers, the Command Center first (id 0). Ids are indexes and never change. */
  readonly towers: Tower[] = [];
  readonly enemies: Enemy[] = [];
  readonly patrols: Patrol[] = [];
  /** The fleet offshore: the ships of the waves that are on, until their launchers are empty and they have sailed out. */
  readonly ships: Ship[] = [];
  private projectiles: Projectile[] = [];
  private queue: Pending[] = [];
  private missions: FireMission[] = [];
  /** How long the shot and event lists were at the end of the last tick: anything past that came from the player since. */
  private shotMark = 0;
  private eventMark = 0;
  private gunShells: GunShell[] = [];
  private strafes: Strafe[] = [];
  /** The tick the gunship overhead leaves (0: none up). */
  private gunshipUntil = 0;
  private readonly gunReady: Record<GunWeapon, number> = { '105': 0, '40': 0, '25': 0 };
  private readonly abilityAt: Record<AbilityKind, number> = { artillery: 0, tank: 0, heli: 0, predator: 0, carrier: 0, a10: 0, ac130: 0, nuke: 0 };
  /** The tower on each tile, or -1. The road is not in here; see road.mask. */
  private readonly occ = new Int16Array(MAP_SIZE * MAP_SIZE).fill(-1);
  /**
   * The next id for anything the snapshot lists as a unit: the enemy, the patrols and the fleet all
   * draw from this one counter, so no two of them ever share an id. The client draws units by id, and
   * when the patrols' ids were a block above the enemy's a long game's enemies grew into that block.
   */
  private nextUnit = 1;
  private nextProjectile = 1;
  private lastAlive = 0;
  private shots: Shot[] = [];
  private events: CombatEvent[] = [];
  /** The game's own events this tick. */
  tdEvents: TdEvent[] = [];
  /** What the player has done, in order: the game's save. */
  readonly log: TdCommand[] = [];
  stateHash = 0x811c9dc5;

  readonly map: TdMap;
  readonly seed: number;

  constructor(map: TdMap, seed: number, config: GameConfig = TD_CONFIG) {
    this.map = map;
    this.seed = seed;
    this.config = config;
    this.road = buildRoad(map);
    this.rng = new Rng(seed);
    this.cash = map.cash;
    this.maxIntegrity = map.integrity;
    const hq = config.buildings.hq!;
    this.towers.push({
      id: 0, type: 'hq', def: hq, x: this.road.hqX, y: this.road.hqY, level: 1, hp: map.integrity, maxHp: map.integrity, alive: true, gone: false,
      cooldown: 0, burstPos: 0, ammo: Infinity, targetId: -1, missed: new Set(), mode: 'first', priority: [], fired: false, firedTick: -1,
    });
    this.occupy(0, this.road.hqX, this.road.hqY, HQ_SIZE);
  }

  /** The Command Center's integrity: what is left of it. */
  get integrity(): number {
    return this.towers[0]!.hp;
  }

  /** The wave with this number, at this map's difficulty: DEFCON 1 goes nuclear. */
  waveAt(n: number): TdWave {
    return tdWave(n, this.map.nuclear);
  }

  /** The wave that is on, or the next one to come. */
  get currentWave(): TdWave {
    return this.waveAt(Math.max(1, this.phase === 'wave' ? this.wave : this.wave + 1));
  }

  /** The enemies still to step onto the road, and what the fleet has still to launch. */
  get remaining(): number {
    return this.queue.length + this.ships.reduce((n, s) => n + s.left, 0);
  }

  // ---- building ------------------------------------------------------------------------

  private occupy(id: number, x: number, y: number, size: number): void {
    for (let dy = 0; dy < size; dy++) for (let dx = 0; dx < size; dx++) {
      const tx = x + dx, ty = y + dy;
      if (tx >= 0 && ty >= 0 && tx < MAP_SIZE && ty < MAP_SIZE) this.occ[ty * MAP_SIZE + tx] = id;
    }
  }

  private vacate(x: number, y: number, size: number): void {
    for (let dy = 0; dy < size; dy++) for (let dx = 0; dx < size; dx++) {
      const tx = x + dx, ty = y + dy;
      if (tx >= 0 && ty >= 0 && tx < MAP_SIZE && ty < MAP_SIZE) this.occ[ty * MAP_SIZE + tx] = -1;
    }
  }

  /** The tower standing on a tile, or -1. */
  towerAt(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= MAP_SIZE || y >= MAP_SIZE) return -1;
    return this.occ[y * MAP_SIZE + x]!;
  }

  isRoad(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < MAP_SIZE && y < MAP_SIZE && this.road.mask[y * MAP_SIZE + x] === 1;
  }

  /** Why a tower cannot go at (x, y), top-left tile, or null if it can. */
  canPlace(type: string, x: number, y: number): PlaceError | null {
    const def = this.config.buildings[type];
    if (!def || !TOWERS.includes(type)) return 'unknown';
    const size = def.size;
    const lo = def.mine ? 0 : PLACE_MARGIN, hi = def.mine ? MAP_SIZE : MAP_SIZE - PLACE_MARGIN;
    if (x < lo || y < lo || x + size > hi || y + size > hi) return 'off-map';
    for (let dy = 0; dy < size; dy++) for (let dx = 0; dx < size; dx++) {
      const tx = x + dx, ty = y + dy;
      if (tx < SEA_WIDTH) return 'off-map';
      if (this.occ[ty * MAP_SIZE + tx] !== -1) return 'occupied';
      const road = this.isRoad(tx, ty);
      if (def.mine ? !road : road) return def.mine ? 'off-road' : 'on-road';
    }
    if (this.cash < (PRICE[type] ?? Infinity)) return 'poor';
    return null;
  }

  /** Places a tower and pays for it. Returns the new tower's id, or the reason it could not go there. */
  place(type: string, x: number, y: number): number | PlaceError {
    const err = this.canPlace(type, x, y);
    if (err) return err;
    const def = this.config.buildings[type]!;
    const id = this.towers.length;
    this.towers.push({
      id, type, def, x, y, level: 1, hp: hpForLevel(def, 1), maxHp: hpForLevel(def, 1), alive: true, gone: false,
      cooldown: 0, burstPos: 0, ammo: Infinity, targetId: -1, missed: new Set(), mode: 'first', priority: def.defaultPriority ? [...def.defaultPriority] : [], fired: false, firedTick: -1,
    });
    this.occupy(id, x, y, def.size);
    this.cash -= PRICE[type]!;
    this.log.push({ tick: this.tick, kind: 'place', type, x, y });
    return id;
  }

  /** The price of the next level, or null when the tower is at the top, rubble, or not a tower. */
  upgradeCost(id: number): number | null {
    const t = this.towers[id];
    if (!t || id === 0 || t.gone || !t.alive || t.level >= maxLevel(t.type) || t.def.mine) return null;
    return upgradePrice(t.type, t.level + 1);
  }

  upgrade(id: number): boolean {
    const cost = this.upgradeCost(id);
    const t = this.towers[id];
    if (cost === null || !t || this.cash < cost) return false;
    this.cash -= cost;
    this.log.push({ tick: this.tick, kind: 'upgrade', id });
    t.level++;
    // a tower with its own upgrade path takes on the next step: a new gun, new rounds
    t.def = towerDefAt(t.type, t.level);
    t.targetId = -1;
    const max = hpForLevel(t.def, t.level);
    // the extra hit points come with the level, on top of what it has
    t.hp += max - t.maxHp;
    t.maxHp = max;
    return true;
  }

  /** What selling a tower gives back; rubble sells for the ground it stood on (nothing). */
  sellValue(id: number): number {
    const t = this.towers[id];
    if (!t || id === 0 || t.gone) return 0;
    return t.alive ? sellValue(t.type, t.level) : 0;
  }

  sell(id: number): boolean {
    const t = this.towers[id];
    if (!t || id === 0 || t.gone) return false;
    this.cash += this.sellValue(id);
    this.log.push({ tick: this.tick, kind: 'sell', id });
    t.gone = true;
    t.alive = false;
    this.vacate(t.x, t.y, t.def.size);
    for (const e of this.enemies) if (e.targetId === id) e.targetId = -1;
    return true;
  }

  rebuildCost(id: number): number | null {
    const t = this.towers[id];
    if (!t || id === 0 || t.gone || t.alive) return null;
    return rebuildPrice(t.type);
  }

  /** Puts a destroyed tower back up, at the level it had. */
  rebuild(id: number): boolean {
    const cost = this.rebuildCost(id);
    const t = this.towers[id];
    if (cost === null || !t || this.cash < cost) return false;
    this.cash -= cost;
    this.log.push({ tick: this.tick, kind: 'rebuild', id });
    t.alive = true;
    t.hp = t.maxHp;
    t.cooldown = 0; t.burstPos = 0; t.targetId = -1; t.missed.clear();
    return true;
  }

  setMode(id: number, mode: TargetMode): void {
    const t = this.towers[id];
    if (t && id !== 0) { t.mode = mode; t.targetId = -1; this.log.push({ tick: this.tick, kind: 'mode', id, mode }); }
  }

  setPriority(id: number, priority: Threat[]): void {
    const t = this.towers[id];
    if (t && id !== 0) { t.priority = [...priority]; t.targetId = -1; this.log.push({ tick: this.tick, kind: 'priority', id, priority: [...priority] }); }
  }

  // ---- waves ---------------------------------------------------------------------------

  /** Sends the next wave. During a wave it stacks on the one that is on, and pays an early-call bonus. */
  startWave(): TdWave | null {
    if (this.phase === 'lost') return null;
    this.log.push({ tick: this.tick, kind: 'wave' });
    const early = this.phase === 'wave';
    this.wave++;
    const wave = this.waveAt(this.wave);
    const more = this.map.countPct ?? 100;
    for (const g0 of wave.groups) {
      // a harder level sends more of each, closer together, over the same stretch of the wave
      const grp = more === 100 ? g0 : { ...g0, count: Math.ceil((g0.count * more) / 100), gap: Math.max(1, Math.floor((g0.gap * 100) / more)) };
      // each one a little early or late on its mark, up to a third of the gap, so a group does not step out in lockstep
      for (let i = 0; i < grp.count; i++) {
        const jitter = grp.gap >= 3 ? this.rng.int(Math.floor((grp.gap * 2) / 3) + 1) - Math.floor(grp.gap / 3) : 0;
        this.queue.push({ at: Math.max(this.tick, this.tick + grp.at + i * grp.gap + jitter), unit: grp.unit, wave: this.wave });
      }
    }
    this.queue.sort((a, b) => a.at - b.at || a.unit.localeCompare(b.unit));
    this.sail(wave.ships, this.wave);
    if (early) this.cash += Math.floor((waveBonus(this.wave - 1) * EARLY_CALL_BONUS_PCT) / 100);
    this.phase = 'wave';
    this.lastAlive = this.tick;
    this.tdEvents.push({ kind: 'wave', n: this.wave, wave });
    return wave;
  }

  /**
   * Puts one enemy on the map now: the waves call it on their schedule, the tests out of turn. A
   * warhead appears over its aim with its fuse running: half of them, on the sim's own roll, go for
   * the towers where the blast catches the most hit points, the rest for the Command Center; `aim`
   * forces one or the other. Returns the enemy, or null for a type the config does not know.
   */
  spawn(type: string, aim?: WarheadAim, wave = this.wave): Enemy | null {
    const def = this.config.units[type];
    if (!def) return null;
    const warhead = isWarhead(def);
    // a ship is not an enemy on the road: it is launched from, see `sail`; a map with two roads splits the column between them
    if (def.domain === 'sea') return null;
    const roads = this.road.lanes.length;
    const lane = roads > 1 ? this.rng.int(roads) : 0;
    const [sx, sy] = this.road.lanes[lane]!.pts[0]!;
    // on the road a man or a tank is soft: the money goes on what flies
    const hp = Math.floor((def.hp * armourPct(wave) * this.map.armourPct * (isAir(def) ? 100 : GROUND_HP_PCT)) / 1000000);
    // across the road: infantry use the whole width out to the verges, vehicles most of it, things in the air wander most
    const spread = def.infantry ? Math.floor(TILE * 0.9) : isAir(def) ? Math.floor(TILE * 0.8) : Math.floor(TILE * 0.6);
    const off = this.rng.int(spread * 2 + 1) - spread;
    // and each walks or drives at its own pace, a little quicker or slower than the book, so a column strings out
    const pace = PACE_MIN_PCT + this.rng.int(PACE_MAX_PCT - PACE_MIN_PCT + 1);
    const e: Enemy = {
      id: this.nextUnit++, type, def, s: 0, lane, x: sx, y: sy, hp, maxHp: hp, alive: true, through: false, off,
      cooldown: 0, burstPos: 0, targetId: -1, fuse: warhead ? def.warhead!.fuseTicks : -1,
      spawnX: sx, spawnY: sy, lockX: -1, lockY: -1, jinkTicks: 0, jinkDir: 1, aim: null, landed: false, flyAt: -1, wave, pace, route: [],
    };
    if (warhead) this.aimWarhead(e, aim);
    else if (isAir(def)) {
      // anything that flies comes in from any side of the map, free of the road, and heads straight
      // for the Command Center: its spread is across the line of flight, so a flight is not a queue
      const [cx, cy] = this.towerCentre(this.towers[0]!);
      const [ex, ey] = this.skyEntry();
      const d = dist(ex, ey, cx, cy) || 1;
      const ox = Math.trunc((-(cy - ey) * off) / d), oy = Math.trunc(((cx - ex) * off) / d);
      e.x = e.spawnX = ex + ox; e.y = e.spawnY = ey + oy;
      e.lockX = cx + ox; e.lockY = cy + oy;
      e.flyAt = 0;
    } else this.placeOnRoad(e);
    this.enemies.push(e);
    return e;
  }

  /**
   * Where something airborne enters: a point a few tiles beyond a random edge of the map, any
   * side, anywhere along it, so the air comes from every angle and the air defence cannot sit on
   * one line. The sea side counts too: a plane off a carrier comes in over the water.
   */
  private skyEntry(): [x: number, y: number] {
    const side = this.rng.int(4);
    const along = this.rng.int(MAP_SIZE * TILE);
    const out = SKY_APPROACH * TILE;
    switch (side) {
      case 0: return [along, -out];                      // north
      case 1: return [MAP_SIZE * TILE + out, along];     // east
      case 2: return [along, MAP_SIZE * TILE + out];     // south
      default: return [-out, along];                     // west, in off the sea
    }
  }

  /**
   * The way in for something launched off a ship: an entry point beyond the map's edge, anywhere down
   * the coast most often, else round the north or south corner and along that edge, with the corner as
   * a waypoint so the whole flight out there stays over the water and off the map. The far side is out
   * of reach: a drone that flew right round would be a minute coming. Returns the waypoints, the entry last.
   */
  private seaRoute(ship: Ship): [x: number, y: number][] {
    const out = SKY_APPROACH * TILE;
    const side = this.rng.int(4);
    if (side < 2) return [[-out, this.rng.int(MAP_SIZE * TILE)]];   // in off the sea, anywhere along the coast
    // round a corner: not too far along, for the flight back
    const along = this.rng.int(Math.floor(MAP_SIZE * TILE * SEA_ROUTE_REACH));
    const edge = side === 2 ? -out : MAP_SIZE * TILE + out;
    const corner: [number, number] = [-out, edge];
    // a ship already past the corner on the water goes straight for the edge
    return (side === 2 ? ship.y <= edge : ship.y >= edge) ? [[along, edge]] : [corner, [along, edge]];
  }

  /**
   * A warhead appears over its aim with its fuse running: half of them, on the sim's own roll, go for
   * the towers where the blast catches the most hit points, the rest for the Command Center; `aim`
   * forces one or the other. Its arc is drawn from wherever it was spawned (the edge, or a submarine).
   */
  private aimWarhead(e: Enemy, aim?: WarheadAim): void {
    // the roll is taken whatever the aim is forced to, so a forced spawn moves the rng as a free one would
    const roll = this.rng.int(2) === 0;
    const target = (aim ?? (roll ? 'towers' : 'hq')) === 'towers' ? this.towerCluster(e.def.warhead!.radius) : null;
    const [hqX, hqY] = this.towerCentre(this.towers[0]!);
    const [ax, ay] = target ? this.towerCentre(target) : [hqX, hqY];
    e.aim = target ? 'towers' : 'hq';
    e.lockX = ax;
    e.lockY = ay;
    // a nuclear one starts where it was launched and flies in over its fuse; the rest hang over their aim
    if (!e.def.warhead!.nuclear) { e.x = ax; e.y = ay; }
    this.events.push({ kind: 'inbound', what: e.type, x: ax, y: ay, id: e.id, aim: target ? target.type : 'hq', by: e.launcher });
  }

  /**
   * The tower whose centre a blast of radius `r` should fall on to catch the most tower hit points,
   * the Command Center and the mines not counted, or null when there is nothing standing to hit.
   * Ties go to the one built first. The raid's strikes picked their tile the same way.
   */
  private towerCluster(r: number): Tower | null {
    let best: Tower | null = null, bestHp = 0;
    for (const t of this.towers) {
      if (!t.alive || t.gone || t.id === 0 || t.def.mine) continue;
      const [cx, cy] = this.towerCentre(t);
      let hp = 0;
      for (const u of this.towers) {
        if (!u.alive || u.gone || u.id === 0 || u.def.mine) continue;
        if (distToRect(cx, cy, ...this.towerRect(u)) <= r) hp += u.hp;
      }
      if (hp > bestHp) { bestHp = hp; best = t; }
    }
    return best;
  }

  // ---- the fleet -----------------------------------------------------------------------

  /** The wave's ships put to sea: each sails in from the open water to its own station off the coast when its time comes. */
  private sail(ships: readonly TdShip[], wave: number): void {
    const inX = -(FLEET_STATION + FLEET_APPROACH) * TILE;
    // spread down the coast, a little off their marks so two waves' ships do not sit on one another
    ships.forEach((sh, i) => {
      const def = this.config.units[sh.ship];
      if (!def) return;
      const y = Math.floor((MAP_SIZE * TILE * (i + 1)) / (ships.length + 1)) + this.rng.int(TILE * 6 + 1) - TILE * 3;
      this.ships.push({ id: this.nextUnit++, type: sh.ship, def, x: inX, y, unit: sh.unit, left: sh.count, gap: sh.gap, launchIn: Math.floor(sh.gap / 2), state: 'in', at: this.tick + sh.at, wave });
    });
  }

  /**
   * A ship launches one of what it carries: a missile or drone flies at the Command Center free of
   * any road, and the shield has to stop it; a warhead (a submarine's nuclear ICBM) arcs up out of
   * the sea and comes down on its aim with its fuse running, like one from beyond the map.
   */
  private launchAtHq(ship: Ship): void {
    const def = this.config.units[ship.unit];
    if (!def) return;
    const hq = this.towers[0]!;
    const [cx, cy] = this.towerCentre(hq);
    const warhead = isWarhead(def);
    const m: Enemy = {
      id: this.nextUnit++, type: ship.unit, def, s: 0, lane: 0, x: ship.x, y: ship.y, hp: def.hp, maxHp: def.hp, alive: true, through: false, off: 0,
      cooldown: 0, burstPos: 0, targetId: -1, fuse: warhead ? def.warhead!.fuseTicks : -1, spawnX: ship.x, spawnY: ship.y, lockX: cx, lockY: cy, jinkTicks: 0, jinkDir: 1, aim: null, landed: false,
      flyAt: warhead ? -1 : 0, wave: ship.wave, launcher: ship.type, pace: 100, route: [],
    };
    // a missile or drone off a ship does not fly straight in: it goes round over the water and turns in
    // from somewhere along the coast, or round one of the corners, so the fleet's air comes from every
    // bearing it can reach and the shield cannot be stacked on one line from the ship
    if (!warhead) m.route = this.seaRoute(ship);
    this.enemies.push(m);
    this.shots.push({ by: 'unit', type: 'launch', fromX: ship.x, fromY: ship.y, toX: ship.x, toY: ship.y });
    if (warhead) this.aimWarhead(m);
    else this.events.push({ kind: 'inbound', what: ship.unit, x: cx, y: cy, by: ship.type, id: m.id, aim: 'hq' });
  }

  private stepShip(sh: Ship): void {
    if (this.tick < sh.at) return;
    const speed = Math.floor((FLEET_SPEED_TPS * TILE) / TICKS_PER_SECOND);
    const station = -FLEET_STATION * TILE;
    if (sh.state === 'in') {
      sh.x = Math.min(station, sh.x + speed);
      if (sh.x >= station) sh.state = 'station';
      return;
    }
    if (sh.state === 'station') {
      if (sh.left <= 0) { sh.state = 'out'; return; }
      if (--sh.launchIn > 0) return;
      this.launchAtHq(sh);
      sh.left--;
      sh.launchIn = sh.gap;
      return;
    }
    sh.x -= speed;
  }

  private placeOnRoad(e: Enemy): void {
    const [px, py, leg] = roadPoint(this.road, e.s, e.lane);
    const [dx, dy] = legDir(this.road, leg, e.lane);
    // the offset is across the road: the leg's direction turned a quarter
    e.x = px + Math.trunc((-dy * e.off) / 1000);
    e.y = py + Math.trunc((dx * e.off) / 1000);
  }

  // ---- abilities -----------------------------------------------------------------------

  abilityReadyIn(kind: AbilityKind): number {
    return Math.max(0, this.abilityAt[kind] - this.tick);
  }

  /**
   * Whether a support call-in can be made now: off cooldown, paid for, and, for a unit sent in, a
   * wave on for it to fight (it stays for the rest of the wave).
   */
  canCallIn(kind: AbilityKind): boolean {
    const def = this.config.abilities[kind];
    if (!def || this.phase === 'lost' || this.abilityReadyIn(kind) > 0 || this.cash < def.cost) return false;
    if (def.gunship && this.gunshipLeft() > 0) return false;
    return !(def.patrol || def.gunship || def.strafe) || this.phase === 'wave';
  }

  /** Calls support in on a tile: a fire mission, or a unit that hunts round it for the rest of the wave. Returns whether it was taken. */
  ability(kind: AbilityKind, x = 0, y = 0): boolean {
    if (!this.canCallIn(kind)) return false;
    const def = this.config.abilities[kind];
    if (x < 0 || y < 0 || x >= MAP_SIZE || y >= MAP_SIZE) return false;
    const cx = x * TILE + TILE / 2, cy = y * TILE + TILE / 2;
    if (def.patrol) this.fieldPatrol(def.patrol, cx, cy);
    else if (def.strafe) this.strafe(def.strafe, x, y);
    else if (def.gunship) this.gunshipUntil = this.tick + def.gunship.ticks;
    else this.missions.push({ at: this.tick + (def.countdownTicks ?? ARTILLERY_FLIGHT), tx: cx, ty: cy, kind: kind === 'nuke' ? 'nuke' : 'artillery' });
    this.cash -= def.cost;
    this.abilityAt[kind] = this.tick + def.cooldownTicks;
    this.log.push({ tick: this.tick, kind: 'ability', ability: kind, x, y });
    this.event('ability', kind, cx, cy);
    return true;
  }

  // ---- the A-10 ---------------------------------------------------------------------

  /** Lines an A-10's run up along the road through tile (x, y): the nearest road tile, down its leg, in from the end farther from the Command Center. */
  private strafe(def: NonNullable<AbilityDef['strafe']>, x: number, y: number): void {
    let rx = x, ry = y, best = Infinity;
    for (let ty = y - 6; ty <= y + 6; ty++) for (let tx = x - 6; tx <= x + 6; tx++) {
      const d = (tx - x) ** 2 + (ty - y) ** 2;
      if (d < best && this.isRoad(tx, ty)) { best = d; rx = tx; ry = ty; }
    }
    const across = this.isRoad(rx + 1, ry) || this.isRoad(rx - 1, ry);
    let [dx, dy] = across ? [1000, 0] : [0, 1000];
    const [hx, hy] = this.towerCentre(this.towers[0]!);
    const cx = rx * TILE + TILE / 2, cy = ry * TILE + TILE / 2;
    // fly towards the Command Center's side, so the run chases the column in
    if ((hx - cx) * dx + (hy - cy) * dy < 0) { dx = -dx; dy = -dy; }
    const half = Math.floor(def.length / 2);
    const sx = cx - Math.trunc((dx * (half + STRAFE_LEAD)) / 1000), sy = cy - Math.trunc((dy * (half + STRAFE_LEAD)) / 1000);
    this.strafes.push({ id: this.nextUnit++, sx, sy, dx, dy, along: 0, approach: STRAFE_LEAD, fire: def.length, damage: def.damage, width: def.width });
  }

  private strafeAt(st: Strafe): [number, number] {
    return [st.sx + Math.trunc((st.dx * st.along) / 1000), st.sy + Math.trunc((st.dy * st.along) / 1000)];
  }

  /** The A-10s fly on: while over the stretch, every other tick a burst from the GAU-8 hits whatever is in the line a little ahead of it. */
  private stepStrafes(): void {
    if (this.strafes.length === 0) return;
    for (const st of this.strafes) {
      st.along += STRAFE_SPEED;
      const firing = st.along >= st.approach && st.along <= st.approach + st.fire;
      if (!firing || this.tick % 2 !== 0) continue;
      const [px, py] = this.strafeAt(st);
      // the gun bears a tile and a half ahead of the nose
      const ax = px + Math.trunc((st.dx * TILE * 3) / 2000), ay = py + Math.trunc((st.dy * TILE * 3) / 2000);
      this.shots.push({ by: 'defence', type: 'a10', fromX: px, fromY: py, toX: ax, toY: ay });
      for (const e of this.enemies) {
        if (!e.alive || e.fuse >= 0 || (isAir(e.def) && e.def.domain !== 'aircraft')) continue;
        if (dist(e.x, e.y, ax, ay) > st.width) continue;
        // 30 mm depleted uranium: armour takes it twice over
        e.hp -= threatOf(e.def) === 'vehicle' ? st.damage * 2 : st.damage;
        if (e.hp <= 0) { e.hp = 0; e.alive = false; if (isAir(e.def)) this.event('downed', e.type, e.x, e.y, 'a10', e.id); }
      }
      if (this.tick % 4 === 0) this.shots.push({ by: 'defence', type: 'blast', fromX: ax, fromY: ay, toX: ax, toY: ay, blast: st.width, weapon: 'a10' });
    }
    this.strafes = this.strafes.filter(st => st.along < st.approach * 2 + st.fire);
  }

  // ---- the gunship ---------------------------------------------------------------------

  /** Ticks the gunship has left overhead; 0 when there is none. */
  gunshipLeft(): number {
    return Math.max(0, this.gunshipUntil - this.tick);
  }

  /** Ticks until a gun of the gunship can fire again. */
  gunReadyIn(weapon: GunWeapon): number {
    return Math.max(0, this.gunReady[weapon] - this.tick);
  }

  /** The gunner fires one of the guns at a point (units). Returns whether the round went. */
  gunshipFire(weapon: GunWeapon, x: number, y: number): boolean {
    const w = GUNSHIP_WEAPONS[weapon];
    if (!w || this.phase === 'lost' || this.gunshipLeft() === 0 || this.gunReadyIn(weapon) > 0) return false;
    x = Math.trunc(x); y = Math.trunc(y);
    if (x < 0 || y < 0 || x >= MAP_SIZE * TILE || y >= MAP_SIZE * TILE) return false;
    this.gunReady[weapon] = this.tick + w.reloadTicks;
    this.gunShells.push({ at: this.tick + w.flightTicks, x, y, weapon });
    this.shots.push({ by: 'strike', type: 'ac130_fire', fromX: x, fromY: y, toX: x, toY: y, weapon });
    this.log.push({ tick: this.tick, kind: 'gunship', weapon, x, y });
    return true;
  }

  private stepGunShells(): void {
    if (this.gunShells.length === 0) return;
    const keep: GunShell[] = [];
    for (const g of this.gunShells) {
      if (g.at > this.tick) { keep.push(g); continue; }
      const w = GUNSHIP_WEAPONS[g.weapon];
      // the ground first, everything there in the blast; the light guns reach helicopters and drones low over it too
      this.blastEnemies(g.x, g.y, w.radius, w.damage, null, false, `ac130_${g.weapon}`);
      if (w.air) this.blastEnemies(g.x, g.y, w.radius, w.damage, null, true);
    }
    this.gunShells = keep;
  }

  // ---- the enemy -----------------------------------------------------------------------

  private towerRect(t: Tower): [number, number, number, number] {
    return [t.x * TILE, t.y * TILE, t.def.size * TILE, t.def.size * TILE];
  }

  private towerCentre(t: Tower): [number, number] {
    return [t.x * TILE + (t.def.size * TILE) / 2, t.y * TILE + (t.def.size * TILE) / 2];
  }

  private hitTower(t: Tower, damage: number, by: string): void {
    // a tower the player placed is built to last: fire at it is noise, and only the Command Center can be worn down
    if (!t.alive || t.id !== 0) return;
    t.hp -= Math.max(1, Math.floor((damage * TOWER_DAMAGE_PCT) / 100));
    if (t.hp > 0) return;
    t.hp = 0;
    t.alive = false;
    t.targetId = -1;
    const [cx, cy] = this.towerCentre(t);
    this.event('destroyed', t.type, cx, cy, by, t.id);
    this.tdEvents.push({ kind: 'tower-lost', type: t.type, id: t.id });
    for (const e of this.enemies) if (e.targetId === t.id) e.targetId = -1;
    for (const p of this.projectiles) if (p.from === t.id) { /* a missile already flying still flies */ }
  }

  /** A blast on the ground at (x, y): the towers in reach take it, the full damage at the centre and half at the edge. */
  private blastTowers(x: number, y: number, r: number, damage: number, by: string): void {
    for (const t of this.towers) {
      if (!t.alive || t.gone || t.id === 0) continue;
      const dmg = splashDamage(damage, distToRect(x, y, ...this.towerRect(t)), r);
      if (dmg > 0) this.hitTower(t, dmg, by);
    }
  }

  /**
   * A tower's blast at (x, y): the enemy round the target, in the air or on the ground as the target
   * was. One on the ground is on the shot list too, so the view can throw whoever it killed.
   */
  private blastEnemies(x: number, y: number, r: number, damage: number, except: Enemy | null, air: boolean, weapon?: string): void {
    if (!air) this.shots.push({ by: 'defence', type: 'blast', fromX: x, fromY: y, toX: x, toY: y, blast: r, ...(weapon ? { weapon } : {}) });
    for (const e of this.enemies) {
      if (!e.alive || e === except || isAir(e.def) !== air || e.fuse >= 0) continue;
      const dmg = splashDamage(damage, dist(x, y, e.x, e.y), r);
      if (dmg > 0) {
        e.hp -= dmg;
        if (e.hp <= 0) {
          e.hp = 0; e.alive = false;
          if (isAir(e.def)) this.event(e.def.domain === 'missile' ? 'intercepted' : 'downed', e.type, e.x, e.y, undefined, e.id);
        }
      }
    }
  }

  /** Whatever incendiary rounds set alight burns on, losing a little every few ticks until the fire is out or it is dead. */
  private stepBurning(): void {
    for (const e of this.enemies) {
      if (!e.alive || !e.burnTicks || !e.burn) continue;
      e.burnTicks--;
      if (e.burnTicks % e.burn.every !== 0) continue;
      e.hp -= e.burn.dmg;
      if (e.hp <= 0) {
        e.hp = 0; e.alive = false;
        if (isAir(e.def)) this.event('downed', e.type, e.x, e.y, 'fire', e.id);
      }
    }
  }

  /**
   * A Lancet's hunt: it turns for the nearest support unit in sight and goes off on it when it gets
   * there; with none, it turns back for the Command Center. Returns whether it struck.
   */
  private hunt(e: Enemy, speed: number): boolean {
    let prey: Patrol | null = null, best = HUNT_REACH;
    for (const p of this.patrols) {
      if (!p.alive) continue;
      const d = dist(e.x, e.y, p.x, p.y);
      if (d < best) { best = d; prey = p; }
    }
    if (!prey) {
      [e.lockX, e.lockY] = this.towerCentre(this.towers[0]!);
      return false;
    }
    e.lockX = prey.x; e.lockY = prey.y;
    if (best > speed + HUNT_HIT) return false;
    this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: prey.x, toY: prey.y });
    this.hitPatrol(prey, e.def.damage, e.type);
    this.event('detonated', e.type, prey.x, prey.y, undefined, e.id);
    e.alive = false;
    e.landed = true;
    return true;
  }

  /** Something that carries its own explosives going off where it is: a fireball the view can draw. */
  private goOff(e: Enemy): void {
    this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: e.x, toY: e.y });
    this.event('detonated', e.type, e.x, e.y, undefined, e.id);
  }

  private stepEnemy(e: Enemy): void {
    const def = e.def;
    if (e.fuse >= 0) {
      // a warhead coming down on its aim: the Command Center, which it costs integrity, or the towers under it
      if (--e.fuse > 0) {
        // a nuclear one is on its way in, so whatever goes up after it meets it there
        if (def.warhead!.nuclear) [e.x, e.y] = this.warheadAt(e, 0);
        return;
      }
      e.x = e.lockX; e.y = e.lockY;
      e.alive = false;
      if (e.aim === 'towers') e.landed = true; else e.through = true;
      const w = def.warhead!;
      this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: e.x, toY: e.y });
      this.event('impact', e.type, e.x, e.y, undefined, e.id);
      if (w.radius > 0) this.blastTowers(e.x, e.y, w.radius, w.damage, e.type);
      return;
    }
    if (e.jinkTicks > 0) e.jinkTicks--;
    if (e.cooldown > 0) e.cooldown--;
    const speed = Math.floor((def.speed * ENEMY_SPEED_PCT * e.pace * (this.map.speedPct ?? 100)) / 1000000);
    if (def.huntsSupport && e.flyAt >= 0 && e.route.length === 0 && this.hunt(e, speed)) return;
    if (e.flyAt >= 0) {
      // in the air, off the road or off a ship: straight at what it was aimed at, shooting what it passes
      // on the way. `s` is how far it has flown, so a gun set to First takes the one nearest home.
      // a leg of its route first, turning for the next, or for its lock, at each waypoint
      const [wx, wy] = e.route.length > 0 ? e.route[0]! : [e.lockX, e.lockY];
      const d = dist(e.x, e.y, wx, wy);
      e.s += speed;
      if (e.route.length > 0 && d <= speed) {
        e.x = wx; e.y = wy;
        e.route.shift();
        // the last leg is drawn as its own arc, from the turn in
        if (e.route.length === 0) { e.spawnX = e.x; e.spawnY = e.y; }
        return;
      }
      if (d > speed) { e.x += Math.trunc(((wx - e.x) * speed) / d); e.y += Math.trunc(((wy - e.y) * speed) / d); }
      else {
        e.x = e.lockX; e.y = e.lockY;
        e.alive = false;
        if (e.flyAt === 0) {
          // into the Command Center: a leak, costing it integrity; a kamikaze goes up in a fireball on it
          e.through = true;
          if (def.oneWay && def.domain === 'aircraft') this.goOff(e);
          return;
        }
        e.landed = true;
        this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: e.x, toY: e.y });
        this.event('impact', e.type, e.x, e.y, undefined, e.id);
        this.blastTowers(e.x, e.y, def.splash ?? TILE, def.damage, e.type);
        return;
      }
    } else {
      e.s += speed;
      if (e.s >= this.road.lanes[e.lane]!.length - HQ_REACH) {
        e.alive = false; e.through = true;
        if (def.carBomb) this.goOff(e);
        return;
      }
      this.placeOnRoad(e);
    }
    if (def.damage <= 0 || def.recon) return;
    // a one-way drone dives on the first tower it finds under its path: that is what it is for
    if (def.oneWay && def.domain === 'drone' && !def.huntsSupport) {
      for (const t of this.towers) {
        if (!t.alive || t.gone || t.id === 0 || t.def.mine) continue;
        if (distToRect(e.x, e.y, ...this.towerRect(t)) > DIVE_REACH) continue;
        const [cx, cy] = this.towerCentre(t);
        this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: cx, toY: cy });
        this.hitTower(t, def.damage, e.type);
        if (def.splash) this.blastTowers(cx, cy, def.splash, def.damage, e.type);
        e.alive = false;
        this.event('detonated', e.type, cx, cy, undefined, e.id);
        return;
      }
      return;
    }
    if (def.oneWay) return;   // a cruise missile flies on to the Command Center
    if (e.cooldown > 0) return;
    // anything else shoots the nearest tower in reach as it goes by, without stopping
    let target = e.targetId >= 0 ? this.towers[e.targetId]! : null;
    if (target && (!target.alive || target.gone || distToRect(e.x, e.y, ...this.towerRect(target)) > def.range)) target = null;
    if (!target) {
      let bestD = def.range + 1;
      for (const t of this.towers) {
        if (!t.alive || t.gone || t.id === 0 || t.def.mine) continue;
        const d = distToRect(e.x, e.y, ...this.towerRect(t));
        if (d < bestD) { bestD = d; target = t; }
      }
      e.targetId = target ? target.id : -1;
    }
    // a patrol nearer than any tower draws the fire instead
    let patrol: Patrol | null = null, patrolD = target ? distToRect(e.x, e.y, ...this.towerRect(target)) : def.range + 1;
    for (const p of this.patrols) {
      if (!p.alive || !this.canHurtPatrol(def, p)) continue;
      const d = dist(e.x, e.y, p.x, p.y);
      if (d <= def.range && d < patrolD) { patrolD = d; patrol = p; }
    }
    if (patrol) {
      this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: patrol.x, toY: patrol.y });
      const dmg = threatOf(patrol.def) === 'vehicle' && def.antiArmor ? def.damage * def.antiArmor : def.damage;
      this.hitPatrol(patrol, dmg, e.type);
      afterShot(e, def);
      return;
    }
    if (!target) return;
    const [cx, cy] = this.towerCentre(target);
    this.shots.push({ by: 'unit', type: e.type, fromX: e.x, fromY: e.y, toX: cx, toY: cy });
    let damage = def.damage;
    const threat = threatOf(def);
    // concrete shrugs off small arms; a marksman picks off the crews of anything manned
    if (target.def.fortified && SMALL_ARMS.has(threat) && !def.antiArmor) damage = Math.floor(damage / 2);
    if (def.sniper && target.def.manned) damage *= 2;
    this.hitTower(target, damage, e.type);
    if (def.splash) this.blastTowers(cx, cy, def.splash, Math.floor(damage / 2), e.type);
    afterShot(e, def);
  }

  // ---- the patrols ---------------------------------------------------------------------

  /** A support unit sent in to a point: on the spot, or for one off a carrier, flying in from the sea on its row. */
  private fieldPatrol(order: NonNullable<AbilityDef['patrol']>, hx: number, hy: number): void {
    const def = this.config.units[order.unit];
    if (!def) return;
    const [x, y] = order.fromSea ? [SEA_WIDTH * TILE, hy] : [hx, hy];
    this.patrols.push({ id: this.nextUnit++, type: order.unit, hx, hy, radius: order.radius, def, x, y, hp: def.hp, maxHp: def.hp, alive: true, cooldown: 0, burstPos: 0, targetId: -1 });
  }

  /** Whether an enemy's weapon can reach a patrol: a marksman's rifle is no use against armour, a tank's gun cannot be raised at a helicopter. */
  private canHurtPatrol(def: UnitDef, p: Patrol): boolean {
    const threat = threatOf(p.def);
    if (def.cannotHit?.includes(threat)) return false;
    if (isAir(p.def) && !isAir(def) && !def.infantry && def.domain !== 'sea') return false;
    return true;
  }

  private hitPatrol(p: Patrol, damage: number, by: string): void {
    if (!p.alive) return;
    p.hp -= damage;
    if (p.hp > 0) return;
    p.hp = 0;
    p.alive = false;
    this.event(isAir(p.def) ? 'downed' : 'destroyed', p.type, p.x, p.y, by, p.id);
    for (const e of this.enemies) if (e.targetId === p.id) e.targetId = -1;
  }

  private stepPatrol(p: Patrol): void {
    if (!p.alive) return;
    const def = p.def;
    if (p.cooldown > 0) p.cooldown--;
    const { hx, hy, radius } = p;
    const reach = radius + def.range;
    const targets = PATROL_TARGETS[p.type] ?? ['infantry', 'vehicle'];
    const usable = (e: Enemy): boolean => e.alive && e.fuse < 0 && targets.includes(threatOf(e.def)) && dist(hx, hy, e.x, e.y) <= reach;
    let target = p.targetId >= 0 ? this.enemies.find(e => e.id === p.targetId) ?? null : null;
    if (target && !usable(target)) target = null;
    if (!target) {
      let bestD = Infinity;
      for (const e of this.enemies) {
        if (!usable(e)) continue;
        const d = dist(p.x, p.y, e.x, e.y);
        if (d < bestD) { bestD = d; target = e; }
      }
      p.targetId = target ? target.id : -1;
    }
    const speed = Math.min(PATROL_MAX_SPEED, def.speed);
    // towards the target until the gun bears, else home; never past the edge of its ground
    const [gx, gy] = target && dist(p.x, p.y, target.x, target.y) > Math.floor((def.range * 9) / 10) ? [target.x, target.y] : target ? [p.x, p.y] : [hx, hy];
    const d = dist(p.x, p.y, gx, gy);
    if (d > speed) {
      const nx = p.x + Math.trunc(((gx - p.x) * speed) / d), ny = p.y + Math.trunc(((gy - p.y) * speed) / d);
      if (dist(hx, hy, nx, ny) <= radius) { p.x = nx; p.y = ny; }
      else { const dh = dist(p.x, p.y, hx, hy); if (dh > speed) { p.x += Math.trunc(((hx - p.x) * speed) / dh); p.y += Math.trunc(((hy - p.y) * speed) / dh); } }
    } else if (d > 0 && !target) { p.x = gx; p.y = gy; }
    if (!target || p.cooldown !== 0 || dist(p.x, p.y, target.x, target.y) > def.range) return;
    this.shots.push({ by: 'defence', type: p.type, fromX: p.x, fromY: p.y, toX: target.x, toY: target.y });
    let dmg = threatOf(target.def) === 'vehicle' && def.antiArmor ? def.damage * def.antiArmor : def.damage;
    if (!isAir(target.def)) dmg = groundDamage(p.type, dmg);
    target.hp -= dmg;
    const splash = isAir(target.def) ? def.splash ?? 0 : groundBlast(p.type, def.splash);
    if (splash > 0) this.blastEnemies(target.x, target.y, splash, dmg, target, isAir(target.def), p.type);
    if (target.hp <= 0) {
      target.hp = 0; target.alive = false; p.targetId = -1;
      if (isAir(target.def)) this.event(target.def.domain === 'missile' ? 'intercepted' : 'downed', target.type, target.x, target.y, p.type, target.id);
    }
    afterShot(p, def);
  }

  // ---- the towers ----------------------------------------------------------------------

  private canTarget(def: BuildingDef, e: Enemy): boolean {
    return (def.targets ?? ['infantry', 'vehicle']).includes(threatOf(e.def));
  }

  private inFlightAt(id: number): number {
    let n = 0;
    for (const p of this.projectiles) if (!p.coasting && p.targetId === id) n++;
    return n;
  }

  private firedOn(e: Enemy): void {
    if (!e.def.evasion || e.jinkTicks > 0) return;
    e.jinkTicks = JINK_TICKS;
    e.jinkDir = -e.jinkDir;
  }

  private evades(e: Enemy, by: string): boolean {
    if (!e.def.evasion || e.jinkTicks <= 0) return false;
    if (this.rng.int(1000) >= Math.round(e.def.evasion * 1000)) return false;
    this.event('evaded', e.type, e.x, e.y, by, e.id);
    return true;
  }

  /** A round or missile reaching its target: 'hit', or 'miss' (it flies on past). */
  private resolveHit(t: Tower, e: Enemy, damage: number): 'hit' | 'miss' {
    const def = t.def;
    if (!isAir(e.def)) damage = groundDamage(t.type, damage);
    if (def.projectile && this.evades(e, t.type)) return 'miss';
    const chance = interceptChanceVs(def, threatOf(e.def));
    const hits = chance === undefined || this.rng.int(1000) < Math.round(chance * 1000);
    if (hits) {
      e.hp -= damage;
      const splash = isAir(e.def) ? def.splash ?? 0 : groundBlast(t.type, def.splash);
      if (splash > 0) this.blastEnemies(e.x, e.y, splash, damage, e, isAir(e.def), t.type);
      // incendiary rounds set it burning (a fresh hit starts the fire again); explosive rounds burst round it
      if (def.incendiary) { e.burnTicks = def.incendiary.ticks; e.burn = { dmg: def.incendiary.dmg, every: def.incendiary.every }; }
      if (def.explosive && !isAir(e.def)) this.blastEnemies(e.x, e.y, def.explosive.radius, Math.max(1, Math.floor((damage * def.explosive.pct) / 100)), e, false, t.type);
    } else if (def.projectile) {
      t.missed.add(e.id);
      if (t.targetId === e.id) t.targetId = -1;
      if (e.def.domain === 'missile') this.event('missed', e.type, e.x, e.y, t.type, e.id);
    }
    if (e.hp <= 0) {
      e.hp = 0; e.alive = false;
      if (t.targetId === e.id) t.targetId = -1;
      if (isAir(e.def)) this.event(e.def.domain === 'missile' ? 'intercepted' : 'downed', e.type, e.x, e.y, t.type, e.id);
    }
    return hits ? 'hit' : 'miss';
  }

  /**
   * A gun's trigger pull: the round is resolved against the target, and if the gun's rounds carry
   * through ground units, against everyone else along the line behind it, each taking a share of
   * what the one in front stopped. A threat the gun cannot pierce is dense enough to stop the round
   * outright. One tracer is pushed per enemy the round reaches, so the stream reads on screen.
   */
  private resolveGunfire(t: Tower, target: Enemy, damage: number): 'hit' | 'miss' {
    const def = t.def;
    const [cx, cy] = this.towerCentre(t);
    if (!def.pierce || isAir(target.def)) return this.resolveHit(t, target, damage);
    const dx = target.x - cx, dy = target.y - cy;
    const len = Math.hypot(dx, dy) || 1;
    const line: { e: Enemy; along: number }[] = [];
    for (const e of this.enemies) {
      if (!e.alive || isAir(e.def) || !this.canTarget(def, e)) continue;
      const ex = e.x - cx, ey = e.y - cy;
      const along = (ex * dx + ey * dy) / len;
      if (along < 0 || along > len + TILE * 0.9) continue;               // short of the gun, or far past the target
      if (Math.abs(ex * dy - ey * dx) / len > TILE * 0.8) continue;      // not in the round's lane
      line.push({ e, along });
    }
    line.sort((a, b) => a.along - b.along || a.e.id - b.e.id);
    let dmg = damage;
    let first: 'hit' | 'miss' = 'miss';
    for (const { e } of line) {
      const keep = def.pierce[threatOf(e.def)] ?? 0;
      const outcome = this.resolveHit(t, e, dmg);
      if (e.id === target.id) first = outcome;
      if (outcome === 'hit' && e.id !== target.id) {
        // the stream reaches the next man too: a tracer of its own for each the round reaches
        this.shots.push({ by: 'defence', type: t.type, fromX: cx, fromY: cy, toX: e.x, toY: e.y, ...roundOf(def) });
      }
      if (keep <= 0) break;                                  // armour it cannot pierce takes the hit and stops the round
      dmg = Math.max(1, Math.floor((dmg * keep) * 100) / 100);
    }
    return first;
  }

  private garrisonOf(t: Tower): number {
    return Math.ceil((t.hp * t.def.garrison!) / t.maxHp);
  }

  private stepTower(t: Tower): void {
    const def = t.def;
    if (!t.alive || t.gone || def.damage === undefined || def.range === undefined || def.attackTicks === undefined || def.mine) return;
    if (t.cooldown > 0) t.cooldown--;
    const [cx, cy] = this.towerCentre(t);
    const range = rangeAt(def, t.level);
    const saturated = (e: Enemy): boolean => def.salvo !== undefined && this.inFlightAt(e.id) >= def.salvo;
    const usable = (e: Enemy, d: number): boolean =>
      e.alive && this.canTarget(def, e) && TdSim.terminal(e, 0) && !t.missed.has(e.id) && d <= range && d >= (def.minRange ?? 0) && !saturated(e);
    let target = t.targetId >= 0 ? this.enemies.find(e => e.id === t.targetId) ?? null : null;
    if (target && !usable(target, dist(cx, cy, target.x, target.y))) target = null;
    // each new trigger pull picks again, so a gun set to First follows the front of the column
    if (t.burstPos === 0 && t.cooldown === 0) target = null;
    if (!target) {
      let bestKey = Infinity, bestRank = 0x7fffffff, bestD = Infinity;
      for (const e of this.enemies) {
        const d = dist(cx, cy, e.x, e.y);
        if (!usable(e, d)) continue;
        const r = t.priority.indexOf(threatOf(e.def));
        const rank = r < 0 ? t.priority.length : r;
        // a warhead is always first: it is seconds from landing
        const key = e.fuse >= 0 ? -0x3fffffff : t.mode === 'first' ? -e.s : t.mode === 'last' ? e.s : t.mode === 'strong' ? -e.hp : d;
        if (rank < bestRank || (rank === bestRank && (key < bestKey || (key === bestKey && d < bestD)))) { bestRank = rank; bestKey = key; bestD = d; target = e; }
      }
      t.targetId = target ? target.id : -1;
    }
    if (!target || t.cooldown !== 0 || t.ammo <= 0) return;
    const damage = damageAt(def, t.level);
    if (def.pulse) {
      // a microwave pulse: everything it can target in reach is hit at once
      this.shots.push({ by: 'defence', type: t.type, fromX: cx, fromY: cy, toX: cx, toY: cy, blast: range });
      for (const e of this.enemies) if (usable(e, dist(cx, cy, e.x, e.y))) this.resolveHit(t, e, damage);
      t.fired = true;
      t.firedTick = this.tick;
      t.targetId = -1;
      t.cooldown = def.attackTicks;
      return;
    }
    const shooters = def.garrison ? this.garrisonOf(t) : 1;
    for (let k = 0; k < shooters; k++) {
      const off = shooters > 1 ? Math.trunc(((k * 2 - (shooters - 1)) * TILE) / 5) : 0;
      this.shots.push({ by: 'defence', type: t.type, fromX: cx + off, fromY: cy - off, toX: target.x, toY: target.y, ...roundOf(def) });
    }
    if (!def.projectile) this.firedOn(target);
    const fired = this.shots.length - shooters;
    t.ammo--;
    t.fired = true;
    t.firedTick = this.tick;
    afterShot(t, def);
    if (def.projectile) {
      this.projectiles.push({ id: this.nextProjectile++, from: t.id, launchX: cx, launchY: cy, targetId: target.id, x: cx, y: cy, tx: target.x, ty: target.y, speed: def.projectile, damage, vx: 0, vy: 0, coasting: false, coastLeft: 0, burn: Math.ceil((range * 2) / def.projectile) + TICKS_PER_SECOND });
      if (saturated(target)) t.targetId = -1;
      return;
    }
    if (this.resolveGunfire(t, target, damage * shooters) === 'miss') for (let k = fired; k < this.shots.length; k++) if (this.shots[k]!.type === t.type) this.shots[k]!.miss = true;
    if (def.ceaseOnKill && !target.alive) { t.burstPos = 0; t.cooldown = def.attackTicks; }
  }

  private stepMine(t: Tower): void {
    const def = t.def;
    if (!t.alive || t.gone || !def.mine || def.range === undefined) return;
    // a mine re-arms a while after it goes off: it is never spent
    if (t.cooldown > 0) { t.cooldown--; return; }
    const [cx, cy] = this.towerCentre(t);
    for (const e of this.enemies) {
      if (!e.alive || e.fuse >= 0 || isAir(e.def) || !this.canTarget(def, e) || dist(cx, cy, e.x, e.y) > def.range) continue;
      this.shots.push({ by: 'defence', type: t.type, fromX: cx, fromY: cy, toX: cx, toY: cy });
      e.hp -= def.damage ?? 0;
      if (e.hp <= 0) { e.hp = 0; e.alive = false; }
      this.blastEnemies(cx, cy, groundBlast(t.type, def.mine.splash), def.damage ?? 0, e, false, t.type);
      t.cooldown = MINE_REARM_TICKS;
      this.event('detonated', t.type, cx, cy, t.type, t.id);
      return;
    }
  }

  private stepProjectiles(): void {
    const still: Projectile[] = [];
    const lo = -COAST_MARGIN, hi = MAP_SIZE * TILE + COAST_MARGIN;
    for (const p of this.projectiles) {
      if (p.coasting) {
        if (--p.coastLeft < 0) { this.shots.push({ by: 'defence', type: 'selfdestruct', fromX: p.x, fromY: p.y, toX: p.x, toY: p.y }); continue; }
        p.x += p.vx; p.y += p.vy;
        if (p.x >= lo && p.x <= hi && p.y >= lo && p.y <= hi) still.push(p);
        continue;
      }
      const e = this.enemies.find(u => u.id === p.targetId);
      const from = this.towers[p.from]!;
      if (--p.burn < 0) {
        this.shots.push({ by: 'defence', type: 'fizzle', fromX: p.x, fromY: p.y, toX: p.x, toY: p.y });
        if (e?.alive) this.event('missed', e.type, e.x, e.y, from.type, e.id);
        continue;
      }
      if (e?.alive) { p.tx = e.x; p.ty = e.y; }
      if (e?.alive && dist(p.x, p.y, e.x, e.y) <= JINK_RANGE) this.firedOn(e);
      const d = dist(p.x, p.y, p.tx, p.ty);
      if (d > p.speed) {
        p.vx = Math.trunc(((p.tx - p.x) * p.speed) / d);
        p.vy = Math.trunc(((p.ty - p.y) * p.speed) / d);
        p.x += p.vx; p.y += p.vy;
        still.push(p);
        continue;
      }
      if (d > 0) { p.vx = Math.trunc(((p.tx - p.x) * p.speed) / d); p.vy = Math.trunc(((p.ty - p.y) * p.speed) / d); }
      p.x = p.tx; p.y = p.ty;
      const outcome = e?.alive ? this.resolveHit(from, e, p.damage) : 'miss';
      if (outcome === 'hit') { this.shots.push({ by: 'defence', type: 'impact', fromX: p.x, fromY: p.y, toX: p.x, toY: p.y }); continue; }
      p.coasting = true;
      p.coastLeft = SELF_DESTRUCT_TICKS;
      if (p.vx === 0 && p.vy === 0) p.vx = p.speed;
      still.push(p);
    }
    this.projectiles = still;
  }

  private stepMissions(): void {
    const keep: FireMission[] = [];
    for (const m of this.missions) {
      if (m.at > this.tick) { keep.push(m); continue; }
      const def = this.config.abilities[m.kind];
      const nuke = m.kind === 'nuke';
      const r = def.radius ?? TILE * 3, dmg = def.damage ?? 0;
      this.shots.push({ by: 'strike', type: m.kind, fromX: m.tx, fromY: m.ty, toX: m.tx, toY: m.ty, blast: r });
      this.event('shelled', m.kind, m.tx, m.ty);
      for (const e of this.enemies) {
        // shells catch what is on the ground; a nuke takes the sky over it as well
        if (!e.alive || e.fuse >= 0 || (isAir(e.def) && !nuke)) continue;
        const d = splashDamage(dmg, dist(m.tx, m.ty, e.x, e.y), r);
        if (d > 0) {
          e.hp -= d;
          if (e.hp <= 0) {
            e.hp = 0; e.alive = false;
            if (isAir(e.def)) this.event(e.def.domain === 'missile' ? 'intercepted' : 'downed', e.type, e.x, e.y, m.kind, e.id);
          }
        }
      }
      // the fallout comes back on the Command Center: a share whatever, and more the nearer it went off
      if (def.selfHarm) {
        const hq = this.towers[0]!;
        const [hx, hy] = this.towerCentre(hq);
        const reach = r * 2, d = Math.min(reach, dist(m.tx, m.ty, hx, hy));
        const pct = def.selfHarm.basePct + Math.floor((def.selfHarm.nearPct * (reach - d)) / reach);
        const lives = Math.max(1, Math.floor((this.maxIntegrity * pct) / 100));
        hq.hp = Math.max(0, hq.hp - lives);
        this.tdEvents.push({ kind: 'fallout', lives });
      }
    }
    this.missions = keep;
  }

  private event(kind: CombatEvent['kind'], what: string, x: number, y: number, by?: string, id?: number): void {
    this.events.push({ kind, what, x, y, ...(by ? { by } : {}), ...(id !== undefined ? { id } : {}) });
  }

  // ---- the loop ------------------------------------------------------------------------

  /** One tick. Returns true once the Command Center has fallen. */
  step(): boolean {
    if (this.phase === 'lost') return true;
    // what the player did since the last tick (a call-in, a gunship round) is kept for this tick's lists
    this.shots = this.shots.slice(this.shotMark);
    this.events = this.events.slice(this.eventMark);
    const lost = this.stepTick();
    this.shotMark = this.shots.length;
    this.eventMark = this.events.length;
    return lost;
  }

  private stepTick(): boolean {
    this.tick++;
    this.tdEvents = [];
    while (this.queue.length > 0 && this.queue[0]!.at <= this.tick) { const q = this.queue.shift()!; this.spawn(q.unit, undefined, q.wave); }
    for (const sh of this.ships) this.stepShip(sh);
    for (const e of this.enemies) if (e.alive) this.stepEnemy(e);
    this.stepBurning();
    for (const t of this.towers) if (t.def.mine) this.stepMine(t); else this.stepTower(t);
    for (const p of this.patrols) this.stepPatrol(p);
    this.stepProjectiles();
    this.stepMissions();
    this.stepGunShells();
    this.stepStrafes();
    // the fallen pay, the leaks cost
    const hq = this.towers[0]!;
    for (const e of this.enemies) {
      if (e.alive) continue;
      if (e.through) {
        const lives = BREACH[e.type] ?? 1;
        hq.hp = Math.max(0, hq.hp - lives);
        this.leaks++;
        this.tdEvents.push({ kind: 'leak', type: e.type, lives, id: e.id });
      } else if (!e.landed) {
        this.cash += Math.max(1, Math.floor(((REWARD[e.type] ?? 1) * rewardPct(e.wave) * (this.map.payPct ?? 100)) / 10000));
        this.kills++;
      }
    }
    for (let i = this.enemies.length - 1; i >= 0; i--) if (!this.enemies[i]!.alive) this.enemies.splice(i, 1);
    // a ship with empty launchers sails out, and is gone once it is back where it came in
    const gone = -(FLEET_STATION + FLEET_APPROACH) * TILE;
    for (let i = this.ships.length - 1; i >= 0; i--) if (this.ships[i]!.state === 'out' && this.ships[i]!.x <= gone) this.ships.splice(i, 1);
    if (this.enemies.length > 0 || this.queue.length > 0 || this.ships.some(sh => sh.left > 0)) this.lastAlive = this.tick;
    if (hq.hp <= 0) {
      this.phase = 'lost';
      this.tdEvents.push({ kind: 'lost' });
      this.hashState();
      return true;
    }
    if (this.phase === 'wave' && this.queue.length === 0 && this.enemies.length === 0 && this.ships.every(sh => sh.left === 0) && this.projectiles.every(p => p.coasting) && this.tick >= this.lastAlive + WAVE_QUIET_TICKS) {
      // every wave that was on is over at once
      while (this.cleared < this.wave) {
        this.cleared++;
        const bonus = waveBonus(this.cleared);
        this.cash += bonus;
        this.tdEvents.push({ kind: 'cleared', n: this.cleared, bonus });
      }
      this.phase = 'build';
      for (const t of this.towers) t.targetId = -1;
      // the support called in goes home with the wave
      this.patrols.length = 0;
      if (!this.won && this.cleared >= this.map.waves) { this.won = true; this.tdEvents.push({ kind: 'won' }); }
    }
    this.hashState();
    return false;
  }

  private hashState(): void {
    let h = fnv(this.stateHash, this.tick);
    h = fnv(h, this.cash);
    for (const e of this.enemies) { h = fnv(h, e.x); h = fnv(h, e.y); h = fnv(h, e.hp); h = fnv(h, e.burnTicks ?? 0); }
    for (const t of this.towers) { h = fnv(h, t.hp); h = fnv(h, t.gone ? 1 : 0); }
    for (const p of this.patrols) { h = fnv(h, p.x); h = fnv(h, p.y); h = fnv(h, p.hp); }
    for (const sh of this.ships) { h = fnv(h, sh.x); h = fnv(h, sh.y); h = fnv(h, sh.left); }
    for (const p of this.projectiles) { h = fnv(h, p.x); h = fnv(h, p.y); }
    for (const g of this.gunShells) { h = fnv(h, g.x); h = fnv(h, g.y); }
    for (const st of this.strafes) h = fnv(h, st.along);
    this.stateHash = h;
  }

  /**
   * A game played back from its log: the same map, seed and clicks give the same game, so this is
   * how a saved game comes back. Plays to `tick` (or to the last command when it is sooner).
   */
  static replay(map: TdMap, seed: number, log: readonly TdCommand[], tick: number, config: GameConfig = TD_CONFIG): TdSim {
    const sim = new TdSim(map, seed, config);
    for (const c of log) {
      while (sim.tick < c.tick && sim.phase !== 'lost') sim.step();
      switch (c.kind) {
        case 'place': sim.place(c.type, c.x, c.y); break;
        case 'upgrade': sim.upgrade(c.id); break;
        case 'sell': sim.sell(c.id); break;
        case 'rebuild': sim.rebuild(c.id); break;
        case 'mode': sim.setMode(c.id, c.mode); break;
        case 'priority': sim.setPriority(c.id, c.priority); break;
        case 'wave': sim.startWave(); break;
        case 'ability': sim.ability(c.ability, c.x, c.y); break;
        case 'gunship': sim.gunshipFire(c.weapon, c.x, c.y); break;
      }
    }
    while (sim.tick < tick && sim.phase !== 'lost') sim.step();
    return sim;
  }

  /** Ticks of reload left on something, as the share still to go and the whole length, for the bars. */
  private static reloadOf(cooldown: number, total: number): { reload?: number; reloadTicks?: number } {
    return cooldown > 0 && total > 0 ? { reload: Math.min(1, cooldown / total), reloadTicks: total } : {};
  }

  /**
   * The chance a warhead on its way down is stopped before it lands, from the towers that can still
   * reach it: for each, the interceptors it already has in the air at it and the ones it can still
   * get there before the fuse runs out (its reload and burst as they stand, and the flight), no more
   * than its salvo, since it drops a target it has missed; each one hits at the tower's chance against
   * that kind of threat, and enough must hit to use up the warhead's hit points. An estimate for the
   * alert on screen, not the roll the sim makes.
   */
  private interceptOdds(e: Enemy): { odds: number; shooters: number; missed: number } {
    let survive = 1, shooters = 0, missed = 0;
    const threat = threatOf(e.def);
    for (const t of this.towers) {
      const def = t.def;
      // missile batteries, and a laser, whose beam is there the moment it fires
      if (!t.alive || t.gone || def.damage === undefined || def.mine || !this.canTarget(def, e)) continue;
      if (t.missed.has(e.id)) { missed++; continue; }
      const chance = interceptChanceVs(def, threat) ?? 1;
      if (chance <= 0) continue;
      const [cx, cy] = this.towerCentre(t);
      // a nuclear warhead still on its way in may not be in reach, or coming down, yet: the tower opens up once it is
      let ahead = 0, d = dist(cx, cy, e.x, e.y);
      const inReach = (dd: number): boolean => dd <= rangeAt(def, t.level) && dd >= (def.minRange ?? 0);
      const open = (): boolean => inReach(d) && TdSim.terminal(e, ahead);
      if (e.def.warhead!.nuclear) while (!open() && ahead < e.fuse) { ahead++; const [x, y] = this.warheadAt(e, ahead); d = dist(cx, cy, x, y); }
      if (!open()) continue;
      const flight = def.projectile ? Math.ceil(d / def.projectile) : 0;
      let rolls = 0;
      for (const p of this.projectiles) if (!p.coasting && p.from === t.id && p.targetId === e.id) rolls++;
      const cap = def.salvo ?? Infinity;
      let ammo = t.ammo, wait = Math.max(t.cooldown, ahead), pos = t.burstPos;
      while (rolls < cap && ammo > 0 && wait + flight <= e.fuse) {
        rolls++; ammo--; pos++;
        if (pos < (def.burst ?? 1)) wait += def.burstGap ?? 1;
        else { pos = 0; wait += def.attackTicks ?? 1; }
      }
      if (rolls === 0) continue;
      shooters++;
      const needed = Math.ceil(e.hp / Math.max(1, damageAt(def, t.level)));
      survive *= 1 - atLeast(needed, rolls, chance);
    }
    return { odds: 1 - survive, shooters, missed };
  }

  /** Whether a warhead can be engaged `ahead` ticks from now: always, but a nuclear one only once it is coming down. */
  private static terminal(e: Enemy, ahead: number): boolean {
    const w = e.def.warhead;
    return !w?.nuclear || e.fuse < 0 || (e.fuse - ahead) * 100 <= w.fuseTicks * TERMINAL_PCT;
  }

  /** Where a nuclear warhead will be `ahead` ticks from now on its way in to its aim. */
  private warheadAt(e: Enemy, ahead: number): [number, number] {
    const total = e.def.warhead!.fuseTicks, flown = Math.min(total, total - e.fuse + ahead);
    return [e.spawnX + Math.trunc(((e.lockX - e.spawnX) * flown) / total), e.spawnY + Math.trunc(((e.lockY - e.spawnY) * flown) / total)];
  }

  /** The nuclear warheads in the air, for the alert. */
  private warheadsInbound(): WarheadInbound[] {
    const out: WarheadInbound[] = [];
    for (const e of this.enemies) {
      if (!e.alive || e.fuse < 0 || !e.def.warhead) continue;
      // the tower under its aim point, if it was aimed at the towers rather than the Command Center
      let aim = 'hq';
      if (e.aim === 'towers') for (const t of this.towers) {
        if (!t.alive || t.gone || t.id === 0) continue;
        const [cx, cy] = this.towerCentre(t);
        if (cx === e.lockX && cy === e.lockY) { aim = t.type; break; }
      }
      out.push({ id: e.id, type: e.type, ...(e.launcher ? { by: e.launcher } : {}), fromX: e.spawnX, fromY: e.spawnY, x: e.lockX, y: e.lockY, aim, left: e.fuse, total: e.def.warhead.fuseTicks, ...this.interceptOdds(e) });
    }
    return out;
  }

  snapshot(): Snapshot {
    const warheads = this.warheadsInbound();
    const units: UnitSnapshot[] = this.enemies.map(e => ({
      id: e.id, type: e.type, x: e.x, y: e.y, hp: e.hp, maxHp: e.maxHp, disabled: false,
      ...(e.burnTicks ? { burning: true } : {}),
      ...TdSim.reloadOf(e.burstPos === 0 ? e.cooldown : 0, e.def.attackTicks),
      ...(e.fuse >= 0 ? { fuse: e.fuse, fuseTicks: e.def.warhead!.fuseTicks, arc: { fromX: e.spawnX, fromY: e.spawnY, toX: e.lockX, toY: e.lockY } } : {}),
      // a missile is drawn on an arc over its run in, once it has turned for its target; out on its route it flies low, like a drone
      ...(e.flyAt >= 0 && e.def.domain === 'missile' && e.route.length === 0 ? { arc: { fromX: e.spawnX, fromY: e.spawnY, toX: e.lockX, toY: e.lockY } } : {}),
    }));
    // the fleet, drawn with the enemy: ships nothing can hurt have all their hit points, always
    for (const sh of this.ships) if (this.tick >= sh.at) units.push({ id: sh.id, type: sh.type, x: sh.x, y: sh.y, hp: sh.def.hp, maxHp: sh.def.hp, disabled: false });
    return {
      tick: this.tick,
      incoming: [
        ...this.missions.map(m => ({ kind: m.kind, x: Math.floor(m.tx / TILE), y: Math.floor(m.ty / TILE), left: m.at - this.tick })),
        // a nuclear warhead is marked where it will come down, counting down; the warhead itself is the unit drawn on its arc
        ...warheads.map(w => ({ kind: 'nuke' as const, x: Math.floor(w.x / TILE), y: Math.floor(w.y / TILE), left: w.left, carried: true })),
      ],
      warheads,
      clouds: [],
      units,
      patrols: this.patrols.filter(p => p.alive).map((p): UnitSnapshot => ({
        id: p.id, type: p.type, x: p.x, y: p.y, hp: p.hp, maxHp: p.maxHp, disabled: false, defender: true, home: { x: p.hx, y: p.hy },
        ...TdSim.reloadOf(p.burstPos === 0 ? p.cooldown : 0, p.def.attackTicks),
      })).concat(this.strafes.map((st): UnitSnapshot => {
        const [x, y] = this.strafeAt(st);
        return { id: st.id, type: 'a10', x, y, hp: 1, maxHp: 1, disabled: false, defender: true, ...(st.along > st.approach + st.fire ? { egress: true } : {}) };
      })),
      buildings: this.towers.filter(t => !t.gone).map(t => ({
        id: t.id, type: t.type, x: t.x, y: t.y, size: t.def.size, hp: t.hp, maxHp: t.maxHp, disabled: false, level: t.level,
        ...TdSim.reloadOf(t.burstPos === 0 ? t.cooldown : 0, t.def.attackTicks ?? 0),
        // the player's own mines are no secret to them
        ...(t.def.mine ? { revealed: true } : {}),
        ...(t.fired ? { fired: true } : {}),
        ...(t.def.garrison ? { garrison: this.garrisonOf(t) } : {}),
      })),
      shots: this.shots,
      events: this.events,
      projectiles: this.projectiles.map(p => ({
        id: p.id, type: this.towers[p.from]!.type, x: p.x, y: p.y, targetId: p.targetId,
        arc: { fromX: p.launchX, fromY: p.launchY, toX: p.tx, toY: p.ty },
        ...(p.coasting ? { coasting: true } : {}),
      })),
    };
  }
}
